import React, { useState, useRef, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Paper,
  CircularProgress,
  Avatar,
  Chip,
} from '@mui/material';
import {
  SmartToy as ChatbotIcon,
  Send as SendIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { chatbotService } from '../services/chatbot';

const Chatbot: React.FC = () => {
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState<Array<{ role: 'user' | 'bot'; content: string; timestamp: Date }>>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const { data: history = [] } = useQuery({
    queryKey: ['chatHistory'],
    queryFn: chatbotService.getHistory,
  });

  useEffect(() => {
    if (history.length > 0 && messages.length === 0) {
      const historyMessages = history.slice(-10).map((h) => [
        { role: 'user' as const, content: h.user_message, timestamp: new Date(h.created_at) },
        { role: 'bot' as const, content: h.bot_response, timestamp: new Date(h.created_at) },
      ]).flat();
      setMessages(historyMessages);
    }
  }, [history]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMutation = useMutation({
    mutationFn: chatbotService.sendMessage,
    onSuccess: (data) => {
      setMessages((prev) => [
        ...prev,
        { role: 'user', content: message, timestamp: new Date() },
        { role: 'bot', content: data.response || 'I apologize, but I encountered an error.', timestamp: new Date() },
      ]);
      setMessage('');
    },
    onError: () => {
      setMessages((prev) => [
        ...prev,
        { role: 'user', content: message, timestamp: new Date() },
        { role: 'bot', content: 'I apologize, but I encountered an error. Please try again.', timestamp: new Date() },
      ]);
      setMessage('');
    },
  });

  const handleSend = () => {
    if (message.trim() && !sendMutation.isPending) {
      sendMutation.mutate(message.trim());
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const quickPrompts = [
    "I'm hungry",
    "Best card for groceries",
    "Plan a vacation",
    "Where should I shop?",
    "Show me dining offers",
  ];

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #6366f1 0%, #ec4899 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
          }}
        >
          <ChatbotIcon sx={{ color: 'white', fontSize: 36 }} />
        </Box>
        <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
          AI Assistant
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Ask me anything about your cards, spending, or savings opportunities
        </Typography>
      </Box>

      <Card sx={{ borderRadius: 3, boxShadow: 3, height: '70vh', display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 0 }}>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', bgcolor: 'rgba(99, 102, 241, 0.05)' }}>
            <Typography variant="h6" fontWeight={600}>
              Chat with AI Assistant
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Powered by advanced AI to help you maximize rewards
            </Typography>
          </Box>

          <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
            {messages.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <ChatbotIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Start a conversation
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Try asking: "I'm hungry", "Best card for groceries", or "Plan a vacation"
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
                  {quickPrompts.map((prompt, idx) => (
                    <Chip
                      key={idx}
                      label={prompt}
                      onClick={() => setMessage(prompt)}
                      sx={{ cursor: 'pointer' }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {messages.map((msg, idx) => (
              <Box
                key={idx}
                sx={{
                  display: 'flex',
                  justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: msg.role === 'user' ? 'row-reverse' : 'row',
                    alignItems: 'flex-start',
                    gap: 1,
                    maxWidth: '70%',
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: msg.role === 'user' ? 'primary.main' : 'secondary.main',
                      width: 32,
                      height: 32,
                    }}
                  >
                    {msg.role === 'user' ? <PersonIcon /> : <ChatbotIcon />}
                  </Avatar>
                  <Paper
                    sx={{
                      p: 2,
                      bgcolor: msg.role === 'user' ? 'primary.main' : 'grey.100',
                      color: msg.role === 'user' ? 'white' : 'text.primary',
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="body1">{msg.content}</Typography>
                  </Paper>
                </Box>
              </Box>
            ))}

            {sendMutation.isPending && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Avatar sx={{ bgcolor: 'secondary.main', width: 32, height: 32 }}>
                    <ChatbotIcon />
                  </Avatar>
                  <Paper sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 2 }}>
                    <CircularProgress size={20} />
                  </Paper>
                </Box>
              </Box>
            )}

            <div ref={messagesEndRef} />
          </Box>

          <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                fullWidth
                placeholder="Ask me anything..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={sendMutation.isPending}
                size="small"
              />
              <Button
                variant="contained"
                onClick={handleSend}
                disabled={!message.trim() || sendMutation.isPending}
                sx={{
                  background: 'linear-gradient(135deg, #6366f1 0%, #ec4899 100%)',
                  minWidth: 100,
                }}
                startIcon={sendMutation.isPending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
              >
                Send
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default Chatbot;
